import {
  AlertTriangle,
  Bot,
  CheckCircle2,
  FileSpreadsheet,
  History,
  LoaderCircle,
  RotateCcw,
  Send,
  ShieldCheck,
  Sparkles,
  UploadCloud,
  UserRound,
} from '../components/icons';
import { useMemo, useRef, useState, type FormEvent } from 'react';
import {
  activateWorkbookUpload,
  askFinanceAgent,
  previewWorkbookUpload,
  restoreWorkbookVersion,
  runRecoveryDrill,
  type IntegrationStatus,
  type AgentDataContext,
  type AgentEvidence,
  type WorkbookPreview,
} from '../data/financeAdapter';
import { formatCurrency, formatDateTime, formatMonth } from '../domain/format';
import type { FinanceData } from '../domain/types';

interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  warning?: string;
  evidence?: AgentEvidence[];
  dataContext?: AgentDataContext;
  validated?: boolean;
}

const suggestions = [
  'What is the best way to increase revenue right now?',
  'How much are we currently spending on AWS monthly, and how much is it supposed to go down?',
  'What is the original 2026 budget versus the current outlook?',
  'What happens if I keep Brent part time?',
];

const fileSize = (bytes: number) => `${(bytes / 1024 / 1024).toFixed(1)} MB`;

export const AgentView = ({
  data,
  integration,
  onDataActivated,
}: {
  data: FinanceData;
  integration: IntegrationStatus;
  onDataActivated: () => Promise<void>;
}) => {
  const [messages, setMessages] = useState<ChatMessage[]>([{
    id: 'welcome',
    role: 'assistant',
    content: `Ask me about actuals, forecasts, individual cost lines, assumptions, cash, receivables, debt, or a what-if scenario. I will show the period and assumptions behind the answer.`,
  }]);
  const [question, setQuestion] = useState('');
  const [answerPending, setAnswerPending] = useState(false);
  const [chatError, setChatError] = useState<string | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<WorkbookPreview | null>(null);
  const [uploadPending, setUploadPending] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [uploadSuccess, setUploadSuccess] = useState<string | null>(null);
  const [recoveryPending, setRecoveryPending] = useState(false);
  const [recoveryResult, setRecoveryResult] = useState<string | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);
  const agent = integration.agent;
  const workbook = integration.workbook;
  const canMutate = integration.access?.canMutate !== false;

  const conversationHistory = useMemo(() => messages
    .filter(({ id }) => id !== 'welcome')
    .map(({ role, content }) => ({ role, content })), [messages]);

  const submitQuestion = async (event?: FormEvent, suggestedQuestion?: string) => {
    event?.preventDefault();
    const content = (suggestedQuestion ?? question).trim();
    if (!content || answerPending) return;
    const userMessage: ChatMessage = { id: crypto.randomUUID(), role: 'user', content };
    setMessages((current) => [...current, userMessage]);
    setQuestion('');
    setChatError(null);
    setAnswerPending(true);
    try {
      const result = await askFinanceAgent(content, conversationHistory);
      setMessages((current) => [...current, {
        id: crypto.randomUUID(),
        role: 'assistant',
        content: result.answer,
        warning: result.warning,
        evidence: result.evidence,
        dataContext: result.dataContext,
        validated: result.quality?.validated,
      }]);
    } catch (error) {
      setChatError(error instanceof Error ? error.message : 'The finance agent could not answer.');
    } finally {
      setAnswerPending(false);
    }
  };

  const validateWorkbook = async () => {
    if (!selectedFile || uploadPending) return;
    setUploadPending(true);
    setUploadError(null);
    setUploadSuccess(null);
    setPreview(null);
    try {
      setPreview(await previewWorkbookUpload(selectedFile));
    } catch (error) {
      setUploadError(error instanceof Error ? error.message : 'The workbook could not be validated.');
    } finally {
      setUploadPending(false);
    }
  };

  const activatePreview = async () => {
    if (!preview || uploadPending) return;
    setUploadPending(true);
    setUploadError(null);
    try {
      await activateWorkbookUpload(preview.token);
      setUploadSuccess(`${preview.fileName} is now the active finance workbook.`);
      setPreview(null);
      setSelectedFile(null);
      if (fileInput.current) fileInput.current.value = '';
      await onDataActivated();
    } catch (error) {
      setUploadError(error instanceof Error ? error.message : 'The workbook could not be activated.');
    } finally {
      setUploadPending(false);
    }
  };

  const restoreVersion = async (id: string, fileName: string) => {
    setUploadPending(true);
    setUploadError(null);
    try {
      await restoreWorkbookVersion(id);
      setUploadSuccess(`${fileName} was restored as the active workbook.`);
      await onDataActivated();
    } catch (error) {
      setUploadError(error instanceof Error ? error.message : 'The prior version could not be restored.');
    } finally {
      setUploadPending(false);
    }
  };

  const verifyRecovery = async () => {
    if (!canMutate || recoveryPending) return;
    setRecoveryPending(true);
    setUploadError(null);
    setRecoveryResult(null);
    try {
      const result = await runRecoveryDrill();
      setRecoveryResult(`Backup ${result.backup.id} restored successfully. Dataset ${result.datasetVersionAfter} was unchanged.`);
      await onDataActivated();
    } catch (error) {
      setUploadError(error instanceof Error ? error.message : 'The backup and restore drill failed.');
    } finally {
      setRecoveryPending(false);
    }
  };

  return (
    <div className="agent-layout">
      <section className="panel agent-chat-panel">
        <div className="panel-heading agent-heading">
          <div>
            <p className="eyebrow">Grounded finance analyst</p>
            <h3>Ask the business</h3>
            <p>Answers use deterministic calculations against the active Deverus dataset.</p>
          </div>
          <span className={`agent-status ${agent.configured ? 'ready' : 'fallback'}`}>
            <Sparkles size={14} />
            {agent.configured ? `AI · ${agent.model}` : 'Rule-based · AI not connected'}
          </span>
        </div>

        {!agent.configured ? (
          <div className="agent-notice" role="status">
            <ShieldCheck size={17} />
            <span><strong>The language model is not connected.</strong> Deterministic calculations remain available, but open-ended reasoning and conversational follow-ups are limited until OPENAI_API_KEY is configured on the server.</span>
          </div>
        ) : null}

        {integration.decisionReadiness?.status === 'blocked' ? (
          <div className="agent-notice readiness-blocked" role="alert">
            <AlertTriangle size={17} />
            <span><strong>Decision-grade answers are blocked.</strong> The assistant will still show historical source evidence, but it will refuse current recommendations and scenarios until the listed reconciliation controls pass.</span>
          </div>
        ) : null}

        <div className="suggestion-row" aria-label="Suggested finance questions">
          {suggestions.map((suggestion) => (
            <button key={suggestion} className="question-chip" onClick={() => void submitQuestion(undefined, suggestion)} disabled={answerPending}>
              {suggestion}
            </button>
          ))}
        </div>

        <div className="chat-thread" aria-live="polite">
          {messages.map((message) => (
            <article key={message.id} className={`chat-message ${message.role}`}>
              <span className="chat-avatar" aria-hidden="true">
                {message.role === 'assistant' ? <Bot size={17} /> : <UserRound size={17} />}
              </span>
              <div>
                <strong>{message.role === 'assistant' ? 'Finance intelligence' : 'You'}</strong>
                <p>{message.content}</p>
                {message.warning ? <small className="chat-warning"><AlertTriangle size={13} />{message.warning}</small> : null}
                {message.role === 'assistant' && message.dataContext ? (
                  <small className="chat-data-context">
                    {message.validated ? <ShieldCheck size={13} /> : <AlertTriangle size={13} />}
                    Actuals through {message.dataContext.actualsThrough ?? 'unknown'} · Dataset {message.dataContext.datasetVersion ?? 'unavailable'}
                  </small>
                ) : null}
                {message.role === 'assistant' && message.evidence?.length ? (
                  <details className="chat-evidence">
                    <summary>Show sources and calculations ({message.evidence.length})</summary>
                    <div className="chat-evidence-list">
                      {message.evidence.map((item, index) => (
                        <article key={`${item.tool ?? 'evidence'}-${item.lineId ?? item.label ?? index}-${index}`}>
                          <strong>{item.label ?? item.lineId ?? item.tool ?? 'Financial evidence'}</strong>
                          {item.value != null ? <span>{formatCurrency(item.value)}{item.basis ? ` · ${item.basis}` : ''}</span> : null}
                          {item.period ? <span>Period: {item.period}</span> : null}
                          {item.source ? <span>Source: {item.source}</span> : null}
                          {item.qboAccountIds?.length ? <span>QuickBooks account: {item.qboAccountIds.join(', ')}</span> : null}
                          {item.calculation ? <span>Calculation: {item.calculation}</span> : null}
                          {item.mappingEvidence ? <span>{item.mappingEvidence}</span> : null}
                          {item.sourceDetail ? <span>{item.sourceDetail}</span> : null}
                          {item.syncedAt ? <span>Synced: {formatDateTime(item.syncedAt)}</span> : null}
                          {item.datasetVersion ? <span>Dataset: {item.datasetVersion}</span> : null}
                          {item.blockingReasons?.map((reason) => <span key={reason}>{reason}</span>)}
                        </article>
                      ))}
                    </div>
                  </details>
                ) : null}
              </div>
            </article>
          ))}
          {answerPending ? (
            <article className="chat-message assistant is-thinking">
              <span className="chat-avatar"><LoaderCircle className="is-spinning" size={17} /></span>
              <div><strong>Finance intelligence</strong><p>Checking the model and calculating the answer…</p></div>
            </article>
          ) : null}
        </div>
        {chatError ? <p className="form-error" role="alert">{chatError}</p> : null}
        <form className="agent-composer" onSubmit={(event) => void submitQuestion(event)}>
          <label>
            <span className="sr-only">Ask a finance question</span>
            <textarea
              value={question}
              onChange={(event) => setQuestion(event.target.value)}
              placeholder="Ask about revenue, costs, assumptions, or a scenario…"
              rows={3}
              maxLength={8000}
            />
          </label>
          <button className="button primary" disabled={!question.trim() || answerPending}>
            <Send size={16} />Ask
          </button>
        </form>
      </section>

      <aside className="agent-data-column">
        <section className="panel workbook-panel">
          <div className="panel-heading">
            <div>
              <p className="eyebrow">Monthly data refresh</p>
              <h3>Upload the workbook</h3>
              <p>{canMutate ? 'Validate first, review the changes, then activate.' : 'Production workbook operations are unavailable in the QA sandbox.'}</p>
            </div>
            <UploadCloud size={21} />
          </div>
          <label className={`file-drop${canMutate ? '' : ' is-disabled'}`} aria-disabled={!canMutate}>
            <FileSpreadsheet size={24} />
            <span>{selectedFile ? selectedFile.name : 'Choose the updated Deverus .xlsx file'}</span>
            <small>{selectedFile ? fileSize(selectedFile.size) : 'Maximum file size: 15 MB'}</small>
            <input
              ref={fileInput}
              type="file"
              accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
              onChange={(event) => {
                setSelectedFile(event.target.files?.[0] ?? null);
                setPreview(null);
                setUploadError(null);
                setUploadSuccess(null);
              }}
              disabled={!canMutate}
            />
          </label>
          <button className="button primary upload-action" onClick={() => void validateWorkbook()} disabled={!canMutate || !selectedFile || uploadPending}>
            {uploadPending && !preview ? <LoaderCircle className="is-spinning" size={16} /> : <ShieldCheck size={16} />}
            Validate workbook
          </button>
          {uploadError ? <p className="form-error" role="alert">{uploadError}</p> : null}
          {uploadSuccess ? <p className="upload-success" role="status"><CheckCircle2 size={15} />{uploadSuccess}</p> : null}

          {preview ? (
            <div className="upload-preview">
              <div className="upload-preview-title">
                <div><strong>Validation passed</strong><span>{preview.summary.importedLineCount} mapped financial lines</span></div>
                <CheckCircle2 size={19} />
              </div>
              <dl className="preview-metrics">
                <div><dt>Actuals through</dt><dd>{formatMonth(preview.summary.newAsOfMonth)}</dd></div>
                <div><dt>Forecast revenue</dt><dd>{formatCurrency(preview.summary.forecastRevenue, { compact: true })}</dd></div>
                <div><dt>Forecast expenses</dt><dd>{formatCurrency(preview.summary.forecastExpenses, { compact: true })}</dd></div>
              </dl>
              {preview.summary.warnings.length ? (
                <div className="preview-warnings">
                  {preview.summary.warnings.map((warning) => <p key={warning}><AlertTriangle size={14} />{warning}</p>)}
                </div>
              ) : null}
              <div className="preview-actions">
                <button className="button ghost" onClick={() => setPreview(null)} disabled={uploadPending}>Cancel</button>
                <button className="button primary" onClick={() => void activatePreview()} disabled={uploadPending}>
                  {uploadPending ? <LoaderCircle className="is-spinning" size={15} /> : <CheckCircle2 size={15} />}
                  Use this workbook
                </button>
              </div>
            </div>
          ) : null}
        </section>

        <section className="panel data-provenance-panel">
          <div className="panel-heading">
            <div><p className="eyebrow">Active data</p><h3>Version history</h3></div>
            <History size={20} />
          </div>
          <div className="active-data-summary">
            <strong>{workbook.active?.fileName ?? 'Original verified workbook'}</strong>
            <span>Actuals through {formatMonth(data.asOfMonth)}</span>
            {workbook.active?.activatedAt ? <small>Activated {formatDateTime(workbook.active.activatedAt)}</small> : null}
          </div>
          {canMutate && !workbook.storage.persistent ? (
            <p className="storage-warning"><AlertTriangle size={14} />Render persistent storage is not attached yet; uploaded versions can be lost during a server replacement.</p>
          ) : canMutate && workbook.storage.restartRecoveryVerified ? (
            <p className="storage-ready"><ShieldCheck size={14} />Durable storage and restart recovery are verified.</p>
          ) : canMutate ? <p className="storage-warning"><AlertTriangle size={14} />The durable disk is writable; restart recovery still needs verification.</p> : null}
          {canMutate ? (
            <button className="button ghost upload-action" onClick={() => void verifyRecovery()} disabled={recoveryPending || !workbook.storage.writeVerified || !workbook.storage.hasManagedState}>
              {recoveryPending ? <LoaderCircle className="is-spinning" size={15} /> : <ShieldCheck size={15} />}
              Run backup and restore drill
            </button>
          ) : null}
          {recoveryResult ? <p className="upload-success" role="status"><CheckCircle2 size={15} />{recoveryResult}</p> : null}
          {workbook.versions.length ? (
            <div className="version-list">
              {workbook.versions.slice(0, 5).map((version) => (
                <div className="version-row" key={version.id}>
                  <div><strong>{version.fileName}</strong><span>{formatMonth(version.asOfMonth)} · {formatDateTime(version.activatedAt)}</span></div>
                  {workbook.active?.id !== version.id ? (
                    <button className="icon-button" onClick={() => void restoreVersion(version.id, version.fileName)} aria-label={`Restore ${version.fileName}`} disabled={!canMutate || uploadPending}>
                      <RotateCcw size={15} />
                    </button>
                  ) : <span className="active-version-badge">Active</span>}
                </div>
              ))}
            </div>
          ) : <p className="empty-version-copy">Monthly uploads will appear here after activation.</p>}
        </section>
      </aside>
    </div>
  );
};
